import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ExclamationTriangleIcon, EnterFullScreenIcon } from '@radix-ui/react-icons';
import { useTheme } from '../Theme/ThemeProvider';
import { useDispatch } from 'react-redux';
import { openImage } from '../../store/chatSlice';
import ExcelJS from 'exceljs';
import { convertFileToPdf } from '../../utils/fileConversion.service';
import { useMsal } from '@azure/msal-react';
import logger from "../../utils/logger";
import { utf8ToBase64 } from '../../utils/base64.utils';


interface FilePreviewProps {
  fileData: string | ArrayBuffer;
  fileName: string;
  fileType?: string;
  onError?: () => void;
  onLoad?: () => void; // Called when image/PDF finishes loading
}

const FilePreviewComponent: React.FC<FilePreviewProps> = ({ 
  fileData, 
  fileName, 
  fileType,
  onError,
  onLoad
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const dispatch = useDispatch();
  const [previewError, setPreviewError] = useState(false);
  const { instance: msalInstance } = useMsal();
  
  // Use refs to store URLs so they persist across re-renders
  const imageUrlRef = useRef<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);
  
  // State for PowerPoint conversion
  const [pptConversionState, setPptConversionState] = useState<{
    status: 'idle' | 'converting' | 'converted' | 'error';
    pdfData?: string;
    error?: string;
  }>({ status: 'idle' });
  
  // State for Excel parsing
  const [excelData, setExcelData] = useState<{
    status: 'idle' | 'parsing' | 'parsed' | 'error';
    csvData?: string;
    error?: string;
  }>({ status: 'idle' });
  
  const initializedRef = useRef<boolean>(false); // Track if URLs have been created

  // Determine file type from filename extension and content-type
  // Special case: Backend may convert Excel to CSV text for preview
  const detectedType = useMemo(() => {
    logger.log('[FilePreview] Detecting type:', { fileName, fileType, fileDataType: typeof fileData });
    
    const ext = fileName.split('.').pop()?.toLowerCase();
    logger.log('[FilePreview] File extension:', ext);
    
    // Check file extension first for most cases
    switch (ext) {
      // Images: PNG, JPG, SVG, PDF (plots)
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
      case 'svg':
        return 'image';
      
      // Spreadsheets and data (all displayed as tables)
      case 'csv':
        return 'csv';
      case 'tsv':
        return 'tsv';
      case 'xlsx':
      case 'xls':
        return 'excel';
      case 'json':
        return 'json';
      
      // Documents
      case 'pdf':
        return 'pdf';
      case 'ppt':
      case 'pptx':
        return 'powerpoint';
      
      // Text and markup (all displayed as text)
      case 'txt':
      case 'log':
      case 'md':
      case 'markdown':
      case 'html':
      case 'htm':
        return 'text';
      
      // Archives (no preview)
      case 'zip':
        return 'archive';
    }
    
    // Special case: Backend may convert Excel to CSV text for preview
    // Only check content-type if extension didn't match
    if (fileType && (
      fileType.includes('text/csv') || 
      fileType.includes('application/csv')
    )) {
      logger.log('[FilePreview] Treating as CSV based on contentType:', fileType);
      return 'csv';
    }
    
    // Default to unknown
    return 'unknown';
  }, [fileName, fileType, fileData]);

  // Convert PowerPoint to PDF for preview
  useEffect(() => {
    if (detectedType !== 'powerpoint' || pptConversionState.status !== 'idle') {
      return;
    }

    const convertPowerPoint = async () => {
      logger.log('[FilePreview] Starting PowerPoint to PDF conversion:', fileName);
      setPptConversionState({ status: 'converting' });

      try {
        // Convert ArrayBuffer to File object
        let fileBlob: Blob;
        if (typeof fileData === 'string') {
          // Base64 string - decode it
          const binaryString = atob(fileData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          fileBlob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        } else {
          fileBlob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        }

        const file = new File([fileBlob], fileName, {
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        });

        // Get conversion endpoint from env
        const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
        const conversionEndpoint = process.env.REACT_APP_FILE_CONVERSION_URL 
          || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;

        // Convert using the existing service
        const result = await convertFileToPdf(file, conversionEndpoint, msalInstance);

        logger.log('[FilePreview] PowerPoint conversion successful:', result.name);
        setPptConversionState({
          status: 'converted',
          pdfData: result.data
        });
      } catch (error) {
        console.error('[FilePreview] PowerPoint conversion failed:', error);
        setPptConversionState({
          status: 'error',
          error: error instanceof Error ? error.message : 'Conversion failed'
        });
      }
    };

    convertPowerPoint();
  }, [detectedType, fileName, fileData, msalInstance, pptConversionState.status]);

  // Parse Excel files
  useEffect(() => {
    if (detectedType !== 'excel' || excelData.status !== 'idle') {
      return;
    }

    const parseExcel = async (data: ArrayBuffer): Promise<string[][]> => {
      try {
        logger.log('[FilePreview] Parsing Excel file:', { fileName, byteLength: data.byteLength });
        
        // Read the workbook with ExcelJS
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        
        // Get the first worksheet
        const worksheet = workbook.worksheets[0];
        
        logger.log('[FilePreview] Excel parsed:', {
          fileName,
          sheetName: worksheet.name,
          sheetCount: workbook.worksheets.length
        });
        
        // Convert to array of arrays
        const rows: string[][] = [];
        worksheet.eachRow((row: any, rowNumber: number) => {
          const rowValues: string[] = [];
          row.eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => {
            // Convert cell value to string
            const value = cell.value;
            if (value === null || value === undefined) {
              rowValues.push('');
            } else if (typeof value === 'object' && 'text' in value) {
              // Rich text
              rowValues.push(value.text || '');
            } else if (typeof value === 'object' && 'result' in value) {
              // Formula
              rowValues.push(String(value.result || ''));
            } else {
              rowValues.push(String(value));
            }
          });
          rows.push(rowValues);
        });
        
        return rows;
      } catch (error) {
        console.error('[FilePreview] Excel parsing error:', error);
        throw error;
      }
    };

    const parseExcelFile = async () => {
      logger.log('[FilePreview] Starting Excel parsing:', fileName);
      setExcelData({ status: 'parsing' });

      try {
        let buffer: ArrayBuffer;
        if (typeof fileData === 'string') {
          // Convert string to ArrayBuffer
          const bytes = new Uint8Array(fileData.length);
          for (let i = 0; i < fileData.length; i++) {
            bytes[i] = fileData.charCodeAt(i);
          }
          buffer = bytes.buffer;
        } else {
          buffer = fileData;
        }

        const rows = await parseExcel(buffer);
        // Properly escape cells containing commas, quotes, or newlines for CSV format
        const csvData = rows.map(row => 
          row.map(cell => {
            // If cell contains comma, quote, or newline, wrap in quotes and escape internal quotes
            if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(',')
        ).join('\n');
        
        logger.log('[FilePreview] Excel parsing successful:', fileName);
        setExcelData({
          status: 'parsed',
          csvData
        });
      } catch (error) {
        console.error('[FilePreview] Excel parsing failed:', error);
        setExcelData({
          status: 'error',
          error: error instanceof Error ? error.message : 'Parsing failed'
        });
      }
    };

    parseExcelFile();
  }, [detectedType, fileName, fileData, excelData.status]);
  
  // Call onLoad after Excel data is parsed and ready to render
  useEffect(() => {
    if (detectedType === 'excel' && excelData.status === 'parsed' && onLoad) {
      // Use requestAnimationFrame to ensure the table has actually rendered
      requestAnimationFrame(() => {
        onLoad();
      });
    }
  }, [detectedType, excelData.status, onLoad]);

  // Create stable blob URLs synchronously during render using useMemo
  // CRITICAL: NO dependencies - create once and never recreate
  // The ref persists across all re-renders, preventing URL recreation on scroll
  const imageUrl = useMemo(() => {
    if (detectedType !== 'image') {
      return null;
    }
    
    // If we already have a URL in the ref, ALWAYS reuse it (prevents re-creation on every render)
    if (imageUrlRef.current) {
      return imageUrlRef.current;
    }
    
    // Only log once when actually creating
    if (!initializedRef.current) {
      logger.log('[FilePreview] Creating image URL:', { fileName, fileDataType: typeof fileData, fileDataLength: typeof fileData === 'string' ? fileData.length : (fileData as ArrayBuffer).byteLength });
    }
    
    let url: string;
    if (typeof fileData === 'string') {
      // Check if it's SVG content (starts with XML or SVG tag)
      const isSvgContent = fileData.trim().startsWith('<svg') || fileData.trim().startsWith('<?xml');
      
      if (isSvgContent) {
        // For SVG, create proper data URL with correct MIME type
        // Use UTF-8 safe encoding in case SVG contains special characters
        url = `data:image/svg+xml;base64,${utf8ToBase64(fileData)}`;
      } else if (fileData.startsWith('data:')) {
        url = fileData;
      } else {
        // Detect image type from file extension for proper MIME type
        const ext = fileName.split('.').pop()?.toLowerCase();
        const mimeType = ext === 'svg' ? 'image/svg+xml' : 
                        ext === 'png' ? 'image/png' :
                        ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' :
                        ext === 'gif' ? 'image/gif' :
                        ext === 'webp' ? 'image/webp' : 'image/png';
        url = `data:${mimeType};base64,${fileData}`;
      }
    } else {
      // Detect MIME type from file extension
      const ext = fileName.split('.').pop()?.toLowerCase();
      const mimeType = ext === 'svg' ? 'image/svg+xml' : 
                      ext === 'png' ? 'image/png' :
                      ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' :
                      ext === 'gif' ? 'image/gif' :
                      ext === 'webp' ? 'image/webp' : 
                      fileType || 'image/png';
      
      const blob = new Blob([fileData], { type: mimeType });
      url = URL.createObjectURL(blob);
      if (!initializedRef.current) {
        logger.log('[FilePreview] Created blob URL for image:', url);
      }
    }
    
    imageUrlRef.current = url;
    initializedRef.current = true;
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies - truly stable across all re-renders

  const pdfUrl = useMemo(() => {
    if (detectedType !== 'pdf') {
      return null;
    }
    
    // If we already have a URL in the ref, ALWAYS reuse it (prevents re-creation on every render)
    if (pdfUrlRef.current) {
      return pdfUrlRef.current;
    }
    
    // Only log once when actually creating
    if (!initializedRef.current) {
      logger.log('[FilePreview] Creating PDF URL:', { 
        fileName, 
        fileDataType: typeof fileData, 
        fileDataLength: typeof fileData === 'string' ? fileData.length : (fileData as ArrayBuffer).byteLength,
        firstBytes: typeof fileData === 'string' 
          ? fileData.substring(0, 50) 
          : new TextDecoder().decode((fileData as ArrayBuffer).slice(0, 50))
      });
    }
    
    let url: string;
    if (typeof fileData === 'string') {
      // Check if it's already a blob URL or data URL
      if (fileData.startsWith('blob:') || fileData.startsWith('data:')) {
        url = fileData;
        if (!initializedRef.current) {
          logger.log('[FilePreview] Using existing URL for PDF:', url.substring(0, 50));
        }
      } else {
        // It's raw PDF content - convert to blob URL
        const bytes = new Uint8Array(fileData.length);
        for (let i = 0; i < fileData.length; i++) {
          bytes[i] = fileData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        url = URL.createObjectURL(blob);
        if (!initializedRef.current) {
          logger.log('[FilePreview] Created blob URL for PDF from raw text:', { url, blobSize: blob.size, bytesLength: bytes.length });
        }
      }
    } else {
      const blob = new Blob([fileData], { type: 'application/pdf' });
      url = URL.createObjectURL(blob);
      if (!initializedRef.current) {
        logger.log('[FilePreview] Created blob URL for PDF from ArrayBuffer:', { url, blobSize: blob.size, arrayBufferLength: (fileData as ArrayBuffer).byteLength });
      }
    }
    
    pdfUrlRef.current = url;
    initializedRef.current = true;
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // NO dependencies - truly stable across all re-renders

  // NOTE: We intentionally do NOT cleanup blob URLs because:
  // 1. React 18 Strict Mode causes unmount/remount in dev, triggering cleanup before browser loads images
  // 2. Blob URLs are lightweight and will be garbage collected when the page unloads
  // 3. Attempting cleanup causes ERR_FILE_NOT_FOUND errors


  const parseCSV = (csvText: string, delimiter: string = ','): string[][] => {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim());
    return lines.map(line => {
      // CSV parser that handles quoted fields with embedded delimiters
      const cells: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote (two quotes in a row)
            current += '"';
            i++; // Skip the next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === delimiter && !inQuotes) {
          // End of cell
          cells.push(current);
          current = '';
        } else {
          current += char;
        }
      }
      cells.push(current);
      return cells;
    });
  };

  const parseJSON = (jsonText: string): string => {
    try {
      logger.log('[FilePreview] Parsing JSON file:', { fileName, textLength: jsonText.length });
      
      const data = JSON.parse(jsonText);
      
      // If it's an array of objects, convert to CSV format
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        const headers = Object.keys(data[0]);
        const rows = data.map(item => headers.map(h => String(item[h] ?? '')));
        const csvRows = [headers, ...rows];
        return csvRows.map(row => row.join(',')).join('\n');
      }
      
      // If it's a single object, convert to key-value pairs
      if (typeof data === 'object' && !Array.isArray(data)) {
        const entries = Object.entries(data);
        const csvRows = [['Key', 'Value'], ...entries.map(([k, v]) => [k, String(v)])];
        return csvRows.map(row => row.join(',')).join('\n');
      }
      
      // For other types, just show as text
      return jsonText;
    } catch (error) {
      console.error('[FilePreview] JSON parsing error:', error);
      // If parsing fails, return as-is for text preview
      return jsonText;
    }
  };

  const renderImagePreview = () => {
    if (!imageUrl) {
      console.error('[FilePreview] No image URL available:', { fileName, detectedType, hasFileData: !!fileData });
      return renderUnsupportedPreview();
    }

    logger.log('[FilePreview] Rendering image with URL:', { fileName, imageUrl: imageUrl.substring(0, 50) });

    return (
      <div 
        onClick={() => dispatch(openImage({ src: imageUrl, type: 'image' }))}
        style={{
          width: '100%',
          maxHeight: '300px',
          overflow: 'hidden',
          borderRadius: '8px',
          marginBottom: '12px',
          border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
          cursor: 'pointer',
          position: 'relative'
        }}
        title="Click to view full size"
      >
        <img 
          src={imageUrl}
          alt={fileName}
          onLoad={() => {
            logger.log('[FilePreview] Image loaded successfully:', fileName);
            onLoad?.(); // Notify parent that image is loaded
          }}
          onError={(e) => {
            console.error('[FilePreview] Image load error:', fileName, e);
            setPreviewError(true);
            onError?.();
          }}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            willChange: 'transform', // Hint browser to optimize rendering
            transform: 'translateZ(0)' // Force GPU acceleration
          }}
        />
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '6px',
          borderRadius: '4px',
          fontSize: '0.85em',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <EnterFullScreenIcon width={16} height={16} />
        </div>
      </div>
    );
  };

  const renderTablePreview = (csvText: string) => {
    try {
      logger.log('[FilePreview] Parsing CSV:', {
        fileName,
        textLength: csvText.length,
        firstChars: csvText.substring(0, 100)
      });
      
      const rows = parseCSV(csvText);
      
      logger.log('[FilePreview] CSV parsed:', {
        fileName,
        rowCount: rows.length,
        columnCount: rows[0]?.length || 0
      });
      
      if (rows.length === 0) {
        console.warn('[FilePreview] CSV has no rows');
        return renderUnsupportedPreview();
      }
      
      const maxRowsPreview = 10;
      const displayRows = rows.slice(0, maxRowsPreview);
      const hasMore = rows.length > maxRowsPreview;

      return (
        <div style={{
          marginBottom: '12px',
          border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            overflowX: 'auto',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9em'
            }}>
              <thead>
                <tr style={{
                  background: isDark ? '#2d2d2d' : '#f5f5f5',
                  position: 'sticky',
                  top: 0
                }}>
                  {rows[0]?.map((header, idx) => (
                    <th key={idx} style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      borderBottom: `2px solid ${isDark ? '#444' : '#d0d0d0'}`,
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.slice(1).map((row, rowIdx) => (
                  <tr key={rowIdx} style={{
                    background: rowIdx % 2 === 0 
                      ? (isDark ? '#1a1a1a' : '#ffffff')
                      : (isDark ? '#222' : '#f9f9f9')
                  }}>
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx} style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                        whiteSpace: 'nowrap'
                      }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div style={{
              padding: '8px',
              textAlign: 'center',
              background: isDark ? '#2d2d2d' : '#f5f5f5',
              borderTop: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
              fontSize: '0.85em',
              color: isDark ? '#aaa' : '#666'
            }}>
              Showing {maxRowsPreview} of {rows.length} rows
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('[FilePreview] CSV parsing error:', error);
      return renderUnsupportedPreview();
    }
  };

  const renderTextPreview = (text: string) => {
    const maxLines = 20;
    const lines = text.split(/\r?\n/);
    const displayLines = lines.slice(0, maxLines);
    const hasMore = lines.length > maxLines;

    return (
      <div style={{
        marginBottom: '12px',
        border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <pre style={{
          padding: '12px',
          margin: 0,
          fontSize: '0.85em',
          background: isDark ? '#1a1a1a' : '#f9f9f9',
          color: isDark ? '#e0e0e0' : '#333',
          maxHeight: '300px',
          overflow: 'auto',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}>
          {displayLines.join('\n')}
        </pre>
        {hasMore && (
          <div style={{
            padding: '8px',
            textAlign: 'center',
            background: isDark ? '#2d2d2d' : '#f5f5f5',
            borderTop: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
            fontSize: '0.85em',
            color: isDark ? '#aaa' : '#666'
          }}>
            Showing {maxLines} of {lines.length} lines
          </div>
        )}
      </div>
    );
  };

  const renderPDFPreview = () => {
    if (!pdfUrl) {
      console.error('[FilePreview] No PDF URL available:', { fileName, detectedType, hasFileData: !!fileData });
      return renderUnsupportedPreview();
    }

    logger.log('[FilePreview] Rendering PDF with URL:', { fileName, pdfUrl: pdfUrl.substring(0, 50) });

    // Add zoom parameter to make PDF start more zoomed out (50% = half size, easier to see full page)
    const pdfUrlWithZoom = `${pdfUrl}#zoom=50`;

    return (
      <div 
        style={{
          marginBottom: '12px',
          border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <iframe
          src={pdfUrlWithZoom}
          onLoad={() => {
            logger.log('[FilePreview] PDF loaded successfully:', fileName);
            onLoad?.(); // Notify parent that PDF is loaded
          }}
          style={{
            width: '100%',
            height: '400px',
            border: 'none',
            display: 'block',
            willChange: 'contents',
            transform: 'translateZ(0)'
          }}
          title={fileName}
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            dispatch(openImage({ src: pdfUrl, type: 'pdf' }));
          }}
          style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '6px',
            borderRadius: '4px',
            fontSize: '0.85em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.85)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
          title="Open in fullscreen"
          aria-label="Open PDF in fullscreen"
        >
          <EnterFullScreenIcon width={16} height={16} />
        </button>
      </div>
    );
  };

  const renderUnsupportedPreview = () => {
    return (
      <div style={{
        marginBottom: '12px',
        padding: '20px',
        border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
        borderRadius: '8px',
        textAlign: 'center',
        background: isDark ? '#1a1a1a' : '#f9f9f9',
        color: isDark ? '#aaa' : '#666'
      }}>
        <ExclamationTriangleIcon 
          width={24} 
          height={24} 
          style={{ 
            marginBottom: '8px',
            color: isDark ? '#888' : '#999'
          }} 
        />
        <div style={{ fontSize: '0.9em' }}>
          Preview not available for this file type
        </div>
        <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
          Use the download button below to view the file
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    if (previewError) {
      return renderUnsupportedPreview();
    }

    try {
      // Log for debugging
      logger.log('[FilePreview] Rendering preview:', {
        fileName,
        detectedType,
        fileDataType: typeof fileData,
        fileDataLength: typeof fileData === 'string' ? fileData.length : fileData.byteLength
      });

      // For text-based files, decode ArrayBuffer if needed
      let textData = '';
      if (detectedType === 'csv' || detectedType === 'tsv' || detectedType === 'text' || detectedType === 'json') {
        if (typeof fileData === 'string') {
          textData = fileData;
        } else {
          try {
            textData = new TextDecoder('utf-8').decode(fileData);
          } catch (decodeError) {
            console.error('[FilePreview] Text decode error:', decodeError);
            return renderUnsupportedPreview();
          }
        }
      }

      switch (detectedType) {
        case 'image':
          return renderImagePreview();
        case 'csv':
          return renderTablePreview(textData);
        case 'tsv':
          // Parse TSV with tab delimiter and render as table
          const tsvRows = parseCSV(textData, '\t');
          const tsvCsv = tsvRows.map(row => row.join(',')).join('\n');
          return renderTablePreview(tsvCsv);
        case 'excel':
          // Use parsed Excel data from state
          if (excelData.status === 'parsing') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <p>Parsing Excel file...</p>
              </div>
            );
          }
          
          if (excelData.status === 'error') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <ExclamationTriangleIcon 
                  width={24} 
                  height={24} 
                  style={{ 
                    display: 'inline-block', 
                    marginBottom: '8px',
                    color: isDark ? '#ff6b6b' : '#d32f2f'
                  }} 
                />
                <p>Failed to parse Excel file</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>{excelData.error}</p>
              </div>
            );
          }
          
          if (excelData.status === 'parsed' && excelData.csvData) {
            return renderTablePreview(excelData.csvData);
          }
          
          // Still parsing
          return null;
        case 'text':
          return renderTextPreview(textData);
        case 'json':
          // Try to parse JSON and render as table
          const csvFromJson = parseJSON(textData);
          const jsonRows = parseCSV(csvFromJson);
          if (jsonRows.length > 1) {
            return renderTablePreview(csvFromJson);
          }
          // Fallback to text if parsing failed or resulted in simple text
          return renderTextPreview(textData);
        case 'archive':
          // No preview for archive files (ZIP)
          return null;
        case 'pdf':
          return renderPDFPreview();
        case 'powerpoint':
          // Show loading state while converting
          if (pptConversionState.status === 'converting') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  margin: '0 auto 12px',
                  border: `3px solid ${isDark ? '#444' : '#d0d0d0'}`,
                  borderTop: '3px solid var(--color-accent)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{ fontSize: '0.9em' }}>
                  Converting PowerPoint to PDF...
                </div>
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            );
          }
          
          // Show error state if conversion failed
          if (pptConversionState.status === 'error') {
            return (
              <div style={{
                marginBottom: '12px',
                padding: '20px',
                border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                borderRadius: '8px',
                textAlign: 'center',
                background: isDark ? '#1a1a1a' : '#f9f9f9',
                color: isDark ? '#aaa' : '#666'
              }}>
                <ExclamationTriangleIcon 
                  width={24} 
                  height={24} 
                  style={{ 
                    marginBottom: '8px',
                    color: isDark ? '#888' : '#999'
                  }} 
                />
                <div style={{ fontSize: '0.9em' }}>
                  Failed to convert PowerPoint to PDF
                </div>
                <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
                  {pptConversionState.error || 'Use the download button below to open the file'}
                </div>
              </div>
            );
          }
          
          // Show converted PDF
          if (pptConversionState.status === 'converted' && pptConversionState.pdfData) {
            // Create PDF URL from converted data
            const pdfBytes = atob(pptConversionState.pdfData);
            const pdfArray = new Uint8Array(pdfBytes.length);
            for (let i = 0; i < pdfBytes.length; i++) {
              pdfArray[i] = pdfBytes.charCodeAt(i);
            }
            const pdfBlob = new Blob([pdfArray], { type: 'application/pdf' });
            const pdfUrl = URL.createObjectURL(pdfBlob);
            
            return (
              <div style={{ marginBottom: '12px' }}>
                {/* Info banner */}
                <div style={{
                  padding: '10px 12px',
                  background: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                  border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                  borderRadius: '8px 8px 0 0',
                  fontSize: '0.85em',
                  color: isDark ? '#93c5fd' : '#2563eb'
                }}>
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                    📄 Text-based PDF Preview
                  </div>
                  <div style={{ opacity: 0.9, lineHeight: '1.4' }}>
                    This shows slide content as text. Visual elements (images, charts, tables, formatting) may not be displayed properly. Download the original PowerPoint for full formatting.
                  </div>
                </div>
                
                {/* PDF viewer */}
                <div 
                  style={{
                    border: `1px solid ${isDark ? '#444' : '#d0d0d0'}`,
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <iframe
                    src={`${pdfUrl}#zoom=50`}
                    onLoad={() => {
                      logger.log('[FilePreview] Converted PowerPoint PDF loaded');
                      if (onLoad) onLoad();
                    }}
                    onError={() => {
                      console.error('[FilePreview] Converted PowerPoint PDF load error');
                      if (onError) onError();
                    }}
                    style={{
                      width: '100%',
                      height: '400px',
                      border: 'none',
                      display: 'block'
                    }}
                    title={`PDF preview of ${fileName}`}
                  />
                  <button
                    type="button"
                    onClick={() => window.open(pdfUrl, '_blank')}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: 'none',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.85)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
                    title="Open in fullscreen"
                    aria-label="Open converted PDF in fullscreen"
                  >
                    <EnterFullScreenIcon width={16} height={16} />
                  </button>
                </div>
              </div>
            );
          }
          
          // Fallback (shouldn't reach here, but just in case)
          return renderUnsupportedPreview();
        default:
          console.warn('[FilePreview] Unknown file type:', detectedType);
          return renderUnsupportedPreview();
      }
    } catch (error) {
      console.error('[FilePreview] Render error:', error);
      return renderUnsupportedPreview();
    }
  };

  return (
    <div style={{ marginTop: '12px', marginBottom: '8px' }}>
      {renderPreview()}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders when parent re-renders
export const FilePreview = React.memo(FilePreviewComponent, (prevProps, nextProps) => {
  // Only re-render if fileName changes (fileData content should be the same for same file)
  return prevProps.fileName === nextProps.fileName && 
         prevProps.fileType === nextProps.fileType;
});